import { BROKER_SYNC_RUN_STATUS_OBJ, BROKER_SYNC_ISSUE_STATUS_OBJ, PROMISE_SETTLED_STATUS_OBJ,
  type TBrokerSyncIssueStatus, type TBrokerSyncIssueKind, type TBrokerSyncReviewQuality,
  type BrokerSyncQualityReviewsResponse } from "@signals/shared";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { qk, useApiQuery } from "../_common";
import type { BrokerSyncAccountStatus, BrokerSyncAccountsResponse, BrokerSyncIssuesResponse, BrokerSyncRunSummary } from "./broker-sync-panel.types";

const STATUS_POLL_INTERVAL_MS = 30_000;

/** Null is confirmed absence; undefined means the legacy response cannot establish history. */
export function lastSuccessfulBrokerRun(account: BrokerSyncAccountStatus): BrokerSyncRunSummary | null | undefined {
  if (account.lastSuccessfulRun === null) return null;
  const run = account.lastSuccessfulRun ?? account.latestRun;
  return run?.status === BROKER_SYNC_RUN_STATUS_OBJ.SUCCEEDED && run.finishedAt ? run : undefined;
}

function financialQueryKeys(portfolioId: string) {
  return [
    qk.portfolio.summaryForPortfolio(portfolioId), qk.portfolio.positionsForPortfolio(portfolioId),
    qk.portfolio.transactionsForPortfolio(portfolioId), qk.portfolio.positionHistoryForPortfolio(portfolioId),
    qk.portfolio.cashflowsForPortfolio(portfolioId), qk.portfolio.incomeEvents.forPortfolio(portfolioId),
    qk.portfolio.accountsForPortfolio(portfolioId), qk.portfolio.compareForPortfolio(portfolioId),
    qk.portfolio.yearlyPerformance.all(portfolioId), qk.portfolio.yearlyPerformance.singleForPortfolio(portfolioId),
    qk.portfolio.snapshots(portfolioId), qk.portfolio.snapshotForPortfolio(portfolioId),
    qk.portfolio.snapshotRevisionsForPortfolio(portfolioId), qk.portfolio.brokerSync.issuesForPortfolio(portfolioId),
    qk.portfolio.brokerSync.qualityReviewsForPortfolio(portfolioId),
  ];
}

/** A new accepted ledger invalidates the older read itself, not just its eventual completion timestamp. */
export async function refreshBrokerSyncFinancialState(queryClient: QueryClient, portfolioId: string): Promise<boolean> {
  const results = await Promise.allSettled(financialQueryKeys(portfolioId).map(async queryKey => {
    await queryClient.cancelQueries({ queryKey });
    await queryClient.invalidateQueries({ queryKey }, { throwOnError: true });
  }));
  return results.every(result => result.status !== PROMISE_SETTLED_STATUS_OBJ.REJECTED);
}

/** One watermark per QueryClient. Keep it until every refresh settles so another poll cannot cancel a slow successful section. */
export function useRefreshPortfolioAfterBrokerSync(portfolioId: string, data: BrokerSyncAccountsResponse | undefined, receivedAt: number, enabled = true) {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled || !data || !portfolioId) return;
    const successes = data.items.flatMap(account => {
      const run = lastSuccessfulBrokerRun(account);
      return run?.finishedAt ? [[account.accountId, run.id ?? JSON.stringify([run.startedAt, run.finishedAt])]] : [];
    }).sort(([left], [right]) => String(left).localeCompare(String(right)));
    if (successes.length === 0) return;
    const acknowledgedKey = qk.portfolio.brokerSync.successfulRuns(portfolioId);
    queryClient.setQueryDefaults(acknowledgedKey, { gcTime: Infinity });
    queryClient.setQueryData(acknowledgedKey, Object.fromEntries(successes));
    const signature = JSON.stringify(successes);
    const reconciledKey = qk.portfolio.brokerSync.reconciled(portfolioId);
    queryClient.setQueryDefaults(reconciledKey, { gcTime: Infinity });
    if (queryClient.getQueryData<string>(reconciledKey) === signature) return;
    queryClient.setQueryData(reconciledKey, signature);
    void refreshBrokerSyncFinancialState(queryClient, portfolioId).then(ok => {
      if (!ok && queryClient.getQueryData<string>(reconciledKey) === signature) {
        queryClient.removeQueries({ queryKey: reconciledKey, exact: true });
      }
    });
  }, [enabled, portfolioId, data, receivedAt, queryClient]);
}

export interface BrokerSyncStatusOptions {
  /** One visible page component owns polling and finance reconciliation; nested observers share the query. */
  poll?: boolean;
}

export function useBrokerSyncStatus(portfolioId: string, { poll = false }: BrokerSyncStatusOptions = {}) {
  const query = useApiQuery<BrokerSyncAccountsResponse>(
    qk.portfolio.brokerSync.accounts(portfolioId),
    `/api/portfolio/broker-sync/accounts?portfolioId=${encodeURIComponent(portfolioId)}`,
    { enabled: !!portfolioId, retry: false, retryOnMount: false, timeoutMs: 30_000, staleTime: STATUS_POLL_INTERVAL_MS,
      refetchInterval: poll ? STATUS_POLL_INTERVAL_MS : false, refetchOnMount: false, refetchOnReconnect: false },
  );
  useRefreshPortfolioAfterBrokerSync(portfolioId, query.data, query.dataUpdatedAt, poll && query.isSuccess);
  return query;
}

/** Compatibility observer for existing consumers; polling ownership stays explicit. */
export function useBrokerSyncAccounts(portfolioId: string, options: BrokerSyncStatusOptions = {}) {
  return useBrokerSyncStatus(portfolioId, options);
}

export interface BrokerSyncReadFilters {
  runId?: string | undefined;
  kind?: TBrokerSyncIssueKind | undefined;
  offset?: number | undefined;
  limit?: number | undefined;
  enabled?: boolean | undefined;
  asOf?: string | undefined;
  from?: string | null | undefined;
  to?: string | null | undefined;
}

export function useBrokerSyncIssues(portfolioId: string, accountId?: string,
  status: TBrokerSyncIssueStatus | null | undefined = BROKER_SYNC_ISSUE_STATUS_OBJ.OPEN,
  filters?: BrokerSyncReadFilters) {
  const params = new URLSearchParams({ portfolioId });
  if (status) params.set("status", status);
  if (accountId) params.set("accountId", accountId);
  const { enabled = true } = filters ?? {};
  const scope = { ...(filters?.runId ? { runId: filters.runId } : {}), ...(filters?.kind ? { kind: filters.kind } : {}),
    ...(filters?.offset !== undefined ? { offset: filters.offset } : {}), ...(filters?.limit !== undefined ? { limit: filters.limit } : {}) };
  for (const [key, value] of Object.entries(scope)) {
    if (value !== undefined && value !== null) params.set(key, String(value));
  }
  // The original three-argument call must keep the existing shared cache identity.
  const queryKey = Object.keys(scope).length > 0
    ? qk.portfolio.brokerSync.issuesScope(portfolioId, { accountId, status: status ?? undefined, ...scope })
    : qk.portfolio.brokerSync.issues(portfolioId, accountId, status ?? undefined);
  return useApiQuery<BrokerSyncIssuesResponse>(queryKey,
    `/api/portfolio/broker-sync/issues?${params.toString()}`, { enabled: !!portfolioId && enabled, retry: false, retryOnMount: false, timeoutMs: 30_000 });
}

export function useBrokerSyncQualityReviews(portfolioId: string, accountId?: string,
  quality?: TBrokerSyncReviewQuality, filters: Omit<BrokerSyncReadFilters, "runId" | "kind"> = {}) {
  const { enabled = true } = filters;
  const scope = { ...(filters.offset !== undefined ? { offset: filters.offset } : {}), ...(filters.limit !== undefined ? { limit: filters.limit } : {}) };
  const params = new URLSearchParams({ portfolioId });
  if (accountId) params.set("accountId", accountId);
  if (quality) params.set("quality", quality);
  for (const [key, value] of Object.entries(scope)) {
    if (value !== undefined && value !== null) params.set(key, String(value));
  }
  return useApiQuery<BrokerSyncQualityReviewsResponse>(
    qk.portfolio.brokerSync.qualityReviews(portfolioId, { accountId, quality, ...scope }),
    `/api/portfolio/broker-sync/quality-reviews?${params.toString()}`,
    { enabled: !!portfolioId && enabled, retry: false, retryOnMount: false, timeoutMs: 30_000 },
  );
}
